import { type Offer } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";

export type PayMembers = NonNullable<Offer["attributes"]["workerVisiblePayMembers"]>;

interface PayBoostConfig {
  enabled: boolean;
  subsidyRuleKeys: string[];
  thresholdRatio: number;
}

interface PayBoostEligibilityParams {
  payBoostConfig: PayBoostConfig | undefined;
  payMembers: PayMembers | undefined;
}

export interface PayBoostEligibilityResult {
  isEligible: boolean;
  originalHourlyRate: number | undefined;
}

/**
 * Determines whether a shift is eligible for the pay boost UI treatment.
 *
 * A shift is eligible if:
 * 1. The feature flag is enabled
 * 2. The shift has pay members data
 * 3. The sum of all subsidy rule amounts is greater than
 *    thresholdRatio × sum of all non-subsidy rule amounts
 *
 * When eligible, returns the original (pre-subsidy) hourly rate,
 * which is the sum of all non-subsidy line items converted from minor units to dollars.
 */
const MINIMUM_ORIGINAL_RATE_IN_DOLLARS = 20;

export function getPayBoostEligibility(
  params: PayBoostEligibilityParams
): PayBoostEligibilityResult {
  const { payBoostConfig, payMembers } = params;

  if (!isDefined(payBoostConfig) || !payBoostConfig.enabled || !isDefined(payMembers)) {
    return { isEligible: false, originalHourlyRate: undefined };
  }

  const { subsidyRuleKeys, thresholdRatio } = payBoostConfig;

  const subsidyRuleKeysSet = new Set(subsidyRuleKeys);

  const subsidySumInMinorUnits = payMembers
    .filter((member) => subsidyRuleKeysSet.has(member.label))
    .reduce((sum, member) => sum + member.amount.amountInMinorUnits, 0);

  const nonSubsidySumInMinorUnits = payMembers
    .filter((member) => !subsidyRuleKeysSet.has(member.label))
    .reduce((sum, member) => sum + member.amount.amountInMinorUnits, 0);

  const nonSubsidySumInDollars = nonSubsidySumInMinorUnits / 100;

  if (
    nonSubsidySumInDollars < MINIMUM_ORIGINAL_RATE_IN_DOLLARS ||
    subsidySumInMinorUnits <= thresholdRatio * nonSubsidySumInMinorUnits
  ) {
    return { isEligible: false, originalHourlyRate: undefined };
  }

  return { isEligible: true, originalHourlyRate: nonSubsidySumInDollars };
}
